import React, { useEffect } from "react";
import logoDark from "../assets/logo-dark.png";
import { NAVY, YELLOW, sans, display, P, InfoCard } from "./Legal/shared";

const HABIT_TRACKER_URL = "https://habit.xenwealth.com.my";
const PAGE_TITLE = "Xenwealth Habit Tracker | Xenwealth";
const PAGE_DESCRIPTION = "Xenwealth Habit Tracker is an internal productivity and activity-management platform for authorised Xenwealth team members, with optional read-only Google Calendar integration.";
const CANONICAL_URL = "https://www.xenwealth.com.my/habit-tracker";

/* Sets tab title / meta description / canonical while this page is mounted, restoring the previous values on unmount (no SEO framework is installed, so this is done with plain DOM APIs to match the site's existing single-page <head> setup). */
function usePageMeta() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = PAGE_TITLE;

    let descTag = document.querySelector('meta[name="description"]');
    const prevDescription = descTag ? descTag.getAttribute("content") : null;
    if (!descTag) {
      descTag = document.createElement("meta");
      descTag.setAttribute("name", "description");
      document.head.appendChild(descTag);
    }
    descTag.setAttribute("content", PAGE_DESCRIPTION);

    let canonicalTag = document.querySelector('link[rel="canonical"]');
    const createdCanonical = !canonicalTag;
    if (!canonicalTag) {
      canonicalTag = document.createElement("link");
      canonicalTag.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalTag);
    }
    canonicalTag.setAttribute("href", CANONICAL_URL);

    return () => {
      document.title = prevTitle;
      if (prevDescription !== null) descTag.setAttribute("content", prevDescription);
      if (createdCanonical) canonicalTag.remove();
    };
  }, []);
}

export default function HabitTracker() {
  usePageMeta();

  return (
    <div style={{ background: "#F7F8FA", color: "#0B1A3A", fontFamily: sans }}>
      <style>{`
        .xw-ht a { text-decoration: none; }
        @media (max-width: 900px) {
          .xw-ht-pad { padding-left: 22px !important; padding-right: 22px !important; }
          .xw-ht-title { font-size: 38px !important; }
          .xw-ht-features { grid-template-columns: 1fr !important; }
          .xw-ht-cta-row { flex-direction: column !important; align-items: flex-start !important; }
          .xw-ht-header-links { display: none !important; }
        }
      `}</style>

      {/* header */}
      <div className="xw-ht" style={{ position: "sticky", top: 0, zIndex: 50, background: "rgba(10,21,48,0.9)", backdropFilter: "blur(14px) saturate(1.3)", WebkitBackdropFilter: "blur(14px) saturate(1.3)", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
        <div className="xw-ht-pad" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "20px", padding: "14px 56px", maxWidth: "1180px", margin: "0 auto" }}>
          <a href="/" style={{ display: "block" }}>
            <img src={logoDark} alt="Xenwealth" style={{ height: "26px", width: "auto", display: "block" }} />
          </a>
          <div style={{ display: "flex", alignItems: "center", gap: "26px" }}>
            <div className="xw-ht-header-links" style={{ display: "flex", alignItems: "center", gap: "26px" }}>
              <a href="/privacy" style={{ fontSize: "13.5px", fontWeight: 600, color: "rgba(255,255,255,0.72)" }}>Privacy Policy</a>
              <a href="/terms" style={{ fontSize: "13.5px", fontWeight: 600, color: "rgba(255,255,255,0.72)" }}>Terms of Service</a>
            </div>
            <a href={HABIT_TRACKER_URL} style={{ fontSize: "13px", fontWeight: 700, color: NAVY, background: YELLOW, padding: "11px 22px", borderRadius: "100px" }}>Agent Login</a>
          </div>
        </div>
      </div>

      {/* hero */}
      <div className="xw-ht" style={{ background: "linear-gradient(180deg,#0A1530 0%,#0E1D3E 100%)", color: "#fff", padding: "76px 56px 68px" }}>
        <div className="xw-ht-pad" style={{ maxWidth: "1180px", margin: "0 auto" }}>
          <div style={{ fontSize: "12px", fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: YELLOW, marginBottom: "18px" }}>Internal Platform</div>
          <h1 className="xw-ht-title" style={{ fontFamily: display, fontWeight: 700, fontSize: "52px", lineHeight: 1.06, letterSpacing: "-0.03em", margin: "0 0 22px", maxWidth: "760px" }}>Xenwealth Habit Tracker</h1>
          <p style={{ fontSize: "18px", lineHeight: 1.65, color: "rgba(255,255,255,0.7)", maxWidth: "640px", margin: "0 0 14px" }}>An internal productivity and activity-management platform built for authorised Xenwealth team members.</p>
          <p style={{ fontSize: "16.5px", lineHeight: 1.7, color: "rgba(255,255,255,0.55)", maxWidth: "640px", margin: 0 }}>Xenwealth Habit Tracker helps our team organise appointments, meetings, recruitment activities, events and other work-related activities in one place.</p>

          <div className="xw-ht-cta-row" style={{ display: "flex", alignItems: "center", gap: "22px", marginTop: "36px", flexWrap: "wrap" }}>
            <a href={HABIT_TRACKER_URL} style={{ display: "inline-block", fontSize: "14.5px", fontWeight: 700, color: NAVY, background: "linear-gradient(180deg,#ffd045,#F4B41A)", padding: "15px 30px", borderRadius: "100px" }}>Agent Login</a>
            <div style={{ display: "flex", gap: "20px" }}>
              <a href="/privacy" style={{ fontSize: "14px", fontWeight: 600, color: "rgba(255,255,255,0.75)" }}>Privacy Policy</a>
              <a href="/terms" style={{ fontSize: "14px", fontWeight: 600, color: "rgba(255,255,255,0.75)" }}>Terms of Service</a>
            </div>
          </div>
          <p style={{ fontSize: "13px", color: "rgba(255,255,255,0.4)", marginTop: "14px" }}>For authorised Xenwealth team members.</p>
        </div>
      </div>

      {/* content */}
      <div className="xw-ht-pad" style={{ maxWidth: "1180px", margin: "0 auto", padding: "64px 56px 20px" }}>
        <div style={{ maxWidth: "760px", marginBottom: "44px" }}>
          <h2 style={{ fontFamily: display, fontWeight: 700, fontSize: "26px", letterSpacing: "-0.02em", margin: "0 0 14px" }}>What Habit Tracker does</h2>
          <P last>Xenwealth Habit Tracker gives authorised team members a single place to plan and record their work activity, with an optional integration that can bring relevant Google Calendar events in automatically.</P>
        </div>

        <div className="xw-ht-features" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "20px", marginBottom: "60px" }}>
          <InfoCard label="Activity Management">Organise and track appointments, meetings, recruitment activities, events and other work-related activities.</InfoCard>
          <InfoCard label="Google Calendar Import">Authorised users may optionally connect Google Calendar and import selected events into Habit Tracker.</InfoCard>
          <InfoCard label="User Control">Users control their Google Calendar connection and may disconnect or revoke access when they choose.</InfoCard>
        </div>

        <div style={{ maxWidth: "760px", marginBottom: "60px" }}>
          <h2 style={{ fontFamily: display, fontWeight: 700, fontSize: "26px", letterSpacing: "-0.02em", margin: "0 0 14px" }}>Google Calendar Integration</h2>
          <P>Xenwealth Habit Tracker may be connected to Google Calendar with the user's permission. Users authorise access through Google's own OAuth consent process — nothing is connected without that explicit step.</P>
          <P>The integration is designed as a one-way, read-only import: Google Calendar → Xenwealth Habit Tracker. Users can configure selected Calendar colours/categories to determine which events are imported, so only matching events come across.</P>
          <div style={{ background: "#fff", border: "1px solid rgba(11,26,58,0.1)", borderRadius: "14px", padding: "18px 20px", margin: "0 0 14px" }}>
            <div style={{ fontSize: "15.5px", fontWeight: 700, color: NAVY }}>Xenwealth Habit Tracker does not create, modify or delete events in your Google Calendar.</div>
          </div>
          <P>Authorised Calendar information may be synchronised manually at your request, or periodically in the background, to keep imported activities up to date.</P>
          <P last>You may disconnect the Google Calendar integration at any time, either within Habit Tracker or through your Google Account permissions.</P>
        </div>

        <div style={{ maxWidth: "760px", marginBottom: "60px" }}>
          <h2 style={{ fontFamily: display, fontWeight: 700, fontSize: "26px", letterSpacing: "-0.02em", margin: "0 0 14px" }}>Privacy &amp; user control</h2>
          <P>Google Calendar information accessed through this integration is used only to provide Habit Tracker functionality. It is not sold, it is not used for advertising, and it is not used to create advertising profiles.</P>
          <P last>Full details on how information is collected, used and retained are set out in our Privacy Policy, alongside the terms that govern use of Habit Tracker.</P>
          <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
            <a href="/privacy" style={{ fontSize: "14px", fontWeight: 700, color: NAVY, background: "#fff", border: "1px solid rgba(11,26,58,0.15)", padding: "12px 22px", borderRadius: "100px" }}>Privacy Policy</a>
            <a href="/terms" style={{ fontSize: "14px", fontWeight: 700, color: NAVY, background: "#fff", border: "1px solid rgba(11,26,58,0.15)", padding: "12px 22px", borderRadius: "100px" }}>Terms of Service</a>
          </div>
        </div>

        <div style={{ maxWidth: "760px", marginBottom: "60px" }}>
          <h2 style={{ fontFamily: display, fontWeight: 700, fontSize: "26px", letterSpacing: "-0.02em", margin: "0 0 14px" }}>Who can use it</h2>
          <P last>Xenwealth Habit Tracker is intended for authorised Xenwealth team members. This page is publicly accessible so it can be viewed without signing in, but the platform itself is not open for public registration.</P>
        </div>

        {/* CTA band */}
        <div style={{ background: NAVY, color: "#fff", borderRadius: "18px", padding: "36px", marginBottom: "60px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "24px", flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: "12px", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: YELLOW, marginBottom: "10px" }}>Xenwealth Habit Tracker</div>
            <div style={{ fontSize: "16.5px", lineHeight: 1.6, color: "rgba(255,255,255,0.78)", maxWidth: "420px" }}>Authorised team members can sign in to Habit Tracker directly.</div>
          </div>
          <a href={HABIT_TRACKER_URL} style={{ display: "inline-block", fontSize: "14.5px", fontWeight: 700, color: NAVY, background: "linear-gradient(180deg,#ffd045,#F4B41A)", padding: "15px 30px", borderRadius: "100px", whiteSpace: "nowrap" }}>Agent Login</a>
        </div>

        <div style={{ maxWidth: "760px", marginBottom: "60px" }}>
          <h2 style={{ fontFamily: display, fontWeight: 700, fontSize: "26px", letterSpacing: "-0.02em", margin: "0 0 14px" }}>Contact &amp; organisation</h2>
          <P last>Xenwealth Habit Tracker is developed and operated by Xenwealth Group.</P>
          <div style={{ fontSize: "16.5px", lineHeight: 1.8, color: "rgba(11,26,58,0.72)" }}>
            Xenwealth Group<br />
            Suite 10-31, Level 10, Lobby 8, Block A,<br />
            Damansara Intan, 47400 Petaling Jaya, Selangor<br /><br />
            <a href="mailto:xenwealthmy@gmail.com" style={{ color: "#b9860a", fontWeight: 600 }}>xenwealthmy@gmail.com</a><br />
            <a href="/" style={{ color: "#b9860a", fontWeight: 600 }}>www.xenwealth.com.my</a>
          </div>
        </div>
      </div>

      {/* footer */}
      <div style={{ padding: "52px 56px 40px", background: "#070F22", color: "#fff", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
        <div className="xw-ht-pad" style={{ maxWidth: "1180px", margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "24px", flexWrap: "wrap" }}>
          <img src={logoDark} alt="Xenwealth" style={{ height: "26px", width: "auto", display: "block" }} />
          <div style={{ display: "flex", gap: "26px", alignItems: "center", flexWrap: "wrap" }}>
            <a href="/" style={{ fontSize: "14px", color: "rgba(255,255,255,0.7)" }}>Home</a>
            <a href="/privacy" style={{ fontSize: "14px", color: "rgba(255,255,255,0.7)" }}>Privacy Policy</a>
            <a href="/terms" style={{ fontSize: "14px", color: "rgba(255,255,255,0.7)" }}>Terms of Service</a>
            <a href={HABIT_TRACKER_URL} style={{ fontSize: "14px", color: "rgba(255,255,255,0.7)" }}>Agent Login</a>
            <span style={{ fontSize: "13px", color: "rgba(255,255,255,0.4)" }}>© 2026 Xenwealth Group</span>
          </div>
        </div>
      </div>
    </div>
  );
}
